import { Icon, Link } from '@amzn/meridian';
import alertTokens from '@amzn/meridian-tokens/base/icon/alert';
import checkCircleFilledTokens from '@amzn/meridian-tokens/base/icon/check-circle-filled';
import pluralize from 'pluralize';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';
import { classes } from 'typestyle';
import { bulkPublishAttributesSummaryPath } from '../../../components/Paths';
import { BULK_ATF_JOB_ID_URL_PARAM } from '../../bulk-publish/constants';
import type { AttributeOrPricingErrorsType } from '../bulkAtf.types';
import { atfModalStyles, uploadAtfMixedSuccessModalStyles } from '../styles/styles';
import { useUploadAtfContext } from '../UploadAtfContext';

const genericError = (
  attributeOrPricingError: AttributeOrPricingErrorsType | undefined,
  errorType: 'formatted' | 'processed' | 'unknown',
) => {
  const specificErrorMessage = `had one or more values
    that were ${errorType === 'unknown' ? 'unknown' : `${errorType} incorrectly.`} Only correctly formatted values were uploaded.`;
  if (!attributeOrPricingError) return '';
  const isEmptyError = !attributeOrPricingError.some((err) => err.columnError.length > 0);
  if (isEmptyError) return '';
  const nestesdSubList = attributeOrPricingError?.map((error) => (
    <ul className={classes(uploadAtfMixedSuccessModalStyles.subList)} data-cy="attribute-error" key="genric-error-key">
      <li key={`${error.asin}-${error.marketplace}`}>
        {error.asin}, {error.marketplace} {`( Row ${error.rowNumber})`}
        <ul>
          {error.columnError.map((nestedError) => (
            <li key={`${nestedError.name}-${nestedError.value}`}>
              {nestedError.name}, {nestedError.value}
            </li>
          ))}
        </ul>
      </li>
    </ul>
  ));
  return attributeOrPricingError ? (
    <div>
      <div className={classes(uploadAtfMixedSuccessModalStyles.iconRow)}>
        {' '}
        <Icon className={atfModalStyles.failureIcon} tokens={alertTokens} />
        <div className={classes(uploadAtfMixedSuccessModalStyles.bigText)}>
          {attributeOrPricingError.length} {pluralize('row', attributeOrPricingError.length)}
          {specificErrorMessage}
        </div>
      </div>
      <div>{nestesdSubList}</div>
    </div>
  ) : (
    ''
  );
};

export const UploadAtfMixedSuccessModal: FC = () => {
  const navigate = useNavigate();
  const { uploadResultTransform, job, bulkPublishJobList } = useUploadAtfContext();
  const successText =
    uploadResultTransform && uploadResultTransform.totalSuccesses > 0 ? (
      <div className={classes(uploadAtfMixedSuccessModalStyles.iconRow)}>
        <Icon className={atfModalStyles.successIcon} tokens={checkCircleFilledTokens} />

        <div className={classes(uploadAtfMixedSuccessModalStyles.bigText)} data-cy="mixed-success-text">
          {uploadResultTransform.totalSuccesses} {pluralize('row', uploadResultTransform.totalSuccesses)} uploaded successfully to Journeys.{' '}
          {bulkPublishJobList.length === 0 ? (
            ''
          ) : (
            <>
              <Link href={`${bulkPublishAttributesSummaryPath}?${BULK_ATF_JOB_ID_URL_PARAM}=${job.id}`} onClick={navigate} type="primary">
                Bulk publishing
              </Link>
              {' has started.'}
            </>
          )}
        </div>
      </div>
    ) : (
      ''
    );
  const golbalErrors = uploadResultTransform?.uploadErrors?.globalErrors ? (
    <div className={classes(uploadAtfMixedSuccessModalStyles.iconRow)}>
      <Icon className={atfModalStyles.failureIcon} tokens={alertTokens} />
      <div className={classes(uploadAtfMixedSuccessModalStyles.bigText)}>
        Global Error: {uploadResultTransform.uploadErrors.globalErrors.errorMessage}{' '}
      </div>
    </div>
  ) : (
    ''
  );
  const asinMarketplaceNotFoundErrors =
    uploadResultTransform?.uploadErrors?.asinMarketplaceErrors && uploadResultTransform?.uploadErrors?.asinMarketplaceErrors.length > 0 ? (
      <div>
        <div className={classes(uploadAtfMixedSuccessModalStyles.iconRow)}>
          {' '}
          <Icon className={atfModalStyles.failureIcon} tokens={alertTokens} />
          <div className={classes(uploadAtfMixedSuccessModalStyles.bigText)} data-cy="asin-error">
            {uploadResultTransform?.uploadErrors?.asinMarketplaceErrors.length}{' '}
            {pluralize('row', uploadResultTransform?.uploadErrors?.asinMarketplaceErrors.length)} failed because the ASIN/Marketplace
            combination(s) do not exist in Journeys
          </div>
        </div>

        <div className={classes(uploadAtfMixedSuccessModalStyles.subText)} data-cy="warning-message-ASIN">
          If needed, work with the product line to get them added to ASIN Central.
          <ul>
            {uploadResultTransform?.uploadErrors?.asinMarketplaceErrors.map((error) => (
              <li key={`${error.asin}-${error.marketplace}`}>
                {error.asin}, {error.marketplace}
              </li>
            ))}
          </ul>
        </div>
      </div>
    ) : (
      ''
    );

  const formattingErrors = genericError(uploadResultTransform?.uploadErrors?.formattingErrors, 'formatted');
  const processingErrors = genericError(uploadResultTransform?.uploadErrors?.processingErrors, 'processed');
  const unknownErrors = genericError(uploadResultTransform?.uploadErrors?.unknownErrors, 'unknown');
  return (
    <div className={classes(uploadAtfMixedSuccessModalStyles.mixedSuccessModal)}>
      {successText}
      {golbalErrors}
      {asinMarketplaceNotFoundErrors}
      {formattingErrors}
      {processingErrors}
      {unknownErrors}
    </div>
  );
};
